import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ExternalLink, KeyRound } from "lucide-react";
import { Shell } from "./clientjob/Shell";
import { ListingEditForm } from "./clientjob/ListingEditForm";
import { PhotoManager } from "./clientjob/PhotoManager";

export default function ClientJob() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(false)).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (loading) {
    return <Shell><div className="flex items-center gap-2 text-slate-400" data-testid="client-job-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading your property website…</div></Shell>;
  }
  if (!job) {
    return <Shell><p className="text-slate-500" data-testid="client-job-not-found">We couldn't find that job.</p></Shell>;
  }

  const delivery = job.delivery || {};
  const liveUrl = delivery.website_published && delivery.gallery_slug ? `/listing/${delivery.gallery_slug}` : null;

  return (
    <Shell>
      {job.archived && (
        <div data-testid="client-job-archived-banner" className="mb-8 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3">
          This job has been archived and is read-only. Changes can no longer be saved here.
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-4 border-b border-border pb-8">
        <div>
          <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-500">Your Property Website</span>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3" data-testid="client-job-address">{job.address?.street}</h1>
          <p className="text-slate-500 mt-1">{[job.address?.city, job.address?.state, job.address?.zip].filter(Boolean).join(", ")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {delivery.download_passcode && (
            <div data-testid="client-download-passcode" className="flex items-center gap-2 border border-[#E7E4DE] rounded-full px-4 py-2.5 text-sm">
              <KeyRound className="h-4 w-4 text-[#C7A361]" strokeWidth={1.5} />
              <span className="text-slate-500">Visitor download passcode:</span>
              <span className="font-mono font-semibold tracking-wider">{delivery.download_passcode}</span>
            </div>
          )}
          {liveUrl && !job.archived ? (
            <a data-testid="client-view-live-site" href={liveUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-none border border-slate-400 font-mono text-[11px] uppercase tracking-[0.1em] px-4 py-2.5 hover:bg-slate-50 transition">
              View Live Site <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
            </a>
          ) : !job.archived ? (
            <span data-testid="client-site-not-published" className="text-[11px] font-mono uppercase tracking-[0.1em] text-slate-400 px-4 py-2.5 border border-dashed border-slate-300">Not published yet</span>
          ) : null}
        </div>
      </div>

      <div className="mt-12 grid lg:grid-cols-[1fr_380px] gap-10 items-start">
        <ListingEditForm job={job} onSaved={setJob} readOnly={!!job.archived} />
        <PhotoManager job={job} onChanged={load} readOnly={!!job.archived} />
      </div>
    </Shell>
  );
}
